/**
 * PixiBakeGraphics — the Pixi implementation of bakeGraphicsToSprite.
 * Uses app.renderer.generateTexture() to render a Graphics into a
 * RenderTexture, then creates a PixiSprite from that texture.
 *
 * Registered at boot by PixiFactoriesRegistry.
 */
import { Graphics, Texture } from 'pixi.js';
import { PixiOverlayHost } from './PixiOverlayHost';
import { PixiSprite, registerBakedTexture } from './PixiSprite';
import type { IGraphics, ISprite, SpriteOptions } from './IRenderer';
import type { BakeOptions } from '../effects/atmosphere/util/bakeGraphicsToSprite';

let bakedKeyCounter = 0;

/**
 * Bake a draw callback's geometry into a RenderTexture → ISprite.
 * Must be called after PixiOverlayHost.ensureReady() has resolved.
 * Returns null if the host isn't ready (degradation path).
 */
export function pixiBakeGraphicsToSprite(opts: BakeOptions): ISprite | null {
  const renderer = PixiOverlayHost.getRenderer();
  if (!renderer) return null; // not ready → degradation

  // 1. Create a temporary Pixi.Graphics + draw the geometry via the callback.
  const pixiGraphics = new Graphics();
  const adapter = makeRawGraphicsAdapter(pixiGraphics);
  opts.draw(adapter);

  // 2. Bake: render the Graphics into a RenderTexture.
  const texture = renderer.generateTexture(pixiGraphics) as Texture | null;
  if (!texture) {
    pixiGraphics.destroy();
    return null;
  }

  // 3. Cache the texture under a unique key so PixiSprite can find it.
  const key = opts.textureKey ?? `__baked_${bakedKeyCounter++}`;
  registerBakedTexture(key, texture);

  // 4. Create the sprite — PixiSprite picks up the cached texture via
  //    initialTextureKey in its build() method.
  const spriteOpts: SpriteOptions = {
    ...opts.spriteOpts,
    initialTextureKey: key,
  };
  const sprite = new PixiSprite(spriteOpts);

  // 5. Clean up the temporary Graphics (geometry is now in the texture).
  pixiGraphics.destroy();

  return sprite;
}

/**
 * Thin IGraphics adapter wrapping a raw Pixi.Graphics — used by the draw
 * callback in bakeGraphicsToSprite. Only implements the draw methods
 * (fillPolygon, strokePolyline, etc.) — transform/alpha are no-ops since
 * the resulting ISprite owns all transforms after baking.
 */
function makeRawGraphicsAdapter(g: Graphics): IGraphics {
  return {
    fillPolygon(points: Array<{ x: number; y: number }>, color: number, alpha: number): void {
      if (points.length < 3) return;
      g.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
      g.closePath();
      g.fill({ color, alpha });
    },
    strokePolyline(points: Array<{ x: number; y: number }>, color: number, width: number, alpha: number): void {
      if (points.length < 2) return;
      g.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) g.lineTo(points[i].x, points[i].y);
      g.stroke({ color, width, alpha });
    },
    fillRect(x: number, y: number, w: number, h: number, color: number, alpha: number): void {
      g.rect(x, y, w, h).fill({ color, alpha });
    },
    // Transform stubs — the sprite owns transforms after baking.
    setPosition(_x: number, _y: number): void { /* no-op */ },
    setAlpha(_a: number): void { /* no-op */ },
    setVisible(_v: boolean): void { /* no-op */ },
    setScale(_sx: number, _sy?: number): void { /* no-op */ },
    setAngle(_deg: number): void { /* no-op */ },
    setBlendMode(_mode: 'normal' | 'add' | 'screen'): void { /* no-op */ },
    setDepth(_d: number): void { /* no-op */ },
    destroy(): void { /* managed by bakeGraphicsToSprite */ },
  } as unknown as IGraphics;
}
